import _ from 'underscore';
import Metadata from './NodeMetadataDefinition';
import PropDef from './_PropertyDefinitionUtil';
import MapUtil from 'gw-portals-util-js/MapUtil';

var typePrefix = 'typekey.';

/* Returns typelist name based on its type. */
function _getTypelistName(type) {
    if (typePrefix !== type.substring(0, typePrefix.length)) {
        throw new Error('Illegal typelist type  name ' + type);
    }
    return type.substring(typePrefix.length);
}

/* Creates a new filter. */
function newFilter(typelist, codeMap, filter) {
    var lookupCode = function lookupCode(code) {
        var codeInfo = MapUtil.get(codeMap, code);
        if (!codeInfo) {
            throw new Error('Bad reference to ' + code + ' from filter ' + filter.name + ' on typelist ' + typelist);
        }
        return codeInfo;
    };

    var referencedCodes = filter.includedCodes.map(lookupCode);

    var res = {
        'name': filter.name,
        'codes': referencedCodes,

        'allows': function allows(codeOrCodeName) {
            return _.some(referencedCodes, function (code) {
                return code === codeOrCodeName || code.code === codeOrCodeName;
            });
        }
    };

    Object.freeze(res);
    Object.freeze(res.codes);

    return res;
}

/* Creates a new typecode draft. */
function newCode(translator, typelist, code) {
    var res = {
        code: code.code,
        priority: code.priority,
        typelist: typelist,
        belongsToCategory: function belongsToCategory(categoryCode) {
            return _.some(code.categories, function (cat) {
                return cat.typecode === categoryCode.code && cat.typelist === categoryCode.typelist.name;
            });
        }
    };
    Object.defineProperty(res, 'name', PropDef.toPropertyDef(translator(code.displayKey)));
    Object.freeze(res);

    return res;
}

/** Creates a new typelist definition.
 * @param {*} xCenter xCenter to which this typelist belongs.
 * @param {Function} translator translation function.
 * @param {String} type typelist's type name.
 * @param {Object} meta typelist metadata.
 * @param {*} [metaType] meta-type of the typelist.
 * @returns {Object}
 */
function newTypelist(xCenter, translator, type, meta, metaType) {
    var res = {
        typeName: type,
        xCenter: xCenter,
        name: _getTypelistName(type),
        metadata: Metadata.createMetadata([], meta.namedMetadata)
    };
    res.codes = meta.codes.map(_.partial(newCode, translator, res));

    /* Fields. */
    if (metaType) {
        res.metaType = metaType;
    }
    var codeMap = MapUtil.toUniqueMap(res.codes, 'code');
    res.filters = meta.filters.map(_.partial(newFilter, type, codeMap));
    var filterMap = MapUtil.toUniqueMap(res.filters, 'name');

    /* Functions. */
    res.getCode = function (code) {
        if (!_.has(codeMap, code)) {
            throw new Error('Typelist ' + type + ' do not have a typecode ' + code);
        }
        return codeMap[code];
    };
    res.getCodesForCategory = function (category) {
        return res.codes.filter(function (code) {
            return code.belongsToCategory(category);
        });
    };
    res.hasCode = function (codeName) {
        return _.has(codeMap, codeName);
    };
    res.getFilter = function (name) {
        if (!_.has(filterMap, name)) {
            throw new Error('Typelist ' + type + ' do not have a filter ' + name);
        }
        return filterMap[name];
    };
    res.hasFilter = function (name) {
        return _.has(filterMap, name);
    };

    Object.freeze(res.codes);
    Object.freeze(res.filters);
    Object.freeze(res);

    return res;
}

export default {
    /**
     * Creates a new typelist representation.
     * Typelist have following properties and methods:
     * <dl>
     *     <dt>name</dt> <dd>simple typelist name.</dd>
     *     <dt>typeName</dt> <dd>typelist's type name.</dd>
     *     <dt>metadata</dt> <dd>metadata defined on the typelist.<dd>
     *     <dt>codes</dt> <dd>array of all codes on the typelist</dd>.
     *     <dt>hasCode(codeName)</dt> <dd>Checks if typecode with the specific name is defined for the typelist.</dd>
     *     <dt>getCode(code)</dt> <dd>returns typecode by its name. Throws an exception if no type code is defined.</dd>
     *     <dt>getCodesForCategory(code)</dt> <dd>returns an array of codes
     *       in a category defined by another typekey.</dd>
     *     <dt>filters</dt> <dd>array of all type filters defined on the typelist</dd>.
     *     <dt>getFilter(filterName)</dt> <dd>returns filter by its name. Throws an exception if filter could not be
     *       found.</dd>
     *     <dt>hasFilter(filterName)</dt><dd>Checks if typelist have a filter with the specific name.</dd>
     *     <dt>xcenter</dt><dd>Reference back to the xcenter to which this dto belongs.</dd>
     * </dl>
     *
     * Typecodes have following methods and properties:
     * <dl>
     *     <dt>code</dt><dd> code of the item</dd>
     *     <dt>priority</dt> <dd> priority of the typecode</dd>
     *     <dt>name</dt> <dd>(translateable) typecode name</dd>
     *     <dt>typelist</dt> <dd>reference back to a typelist instance</dd>
     *     <dt>belongsToCategory(code)<dt> <dd>checks if this typekey belongs to a category</dd>
     * </dl>
     *
     * Each filter have following methods and properties:
     * <dl>
     *     <dt>name</dt> <dd>name of the filter.</dd>
     *     <dt>codes</dt> <dd>typecodes included in the filter.</dd>
     *     <dt>allows(typecodeOrCodeName)</dt><dd> checks if this filter has this typecode in it</dt>
     * </dl>
     *
     * @param {*} xCenter xCenter to which this typelist belongs.
     * @param {Function} translator translator function. This function receives a display key and returns either a value (
     * which could not be changed) or no-arg function used to fetch translated value during the runtime.
     * @param {String} typeName type name.
     * @param {Object} meta typelist metadata.
     * @param {*} [metaType] optional meta-type definition. If present, could define some meta-properties (like
     *   isTypelist, isDTOType, etc...)
     * @returns {Object} typelist information.
     */
    'createTypelistInfo': function createTypelistInfo(xCenter, translator, typeName, meta, metaType) {
        return newTypelist(xCenter, translator, typeName, meta, metaType);
    }
};


//////////////////
// WEBPACK FOOTER
// ./common/js/src/edge/api/gw-portals-metadata-js/_TypelistDefinition.js
// module id = 892
// module chunks = 0